import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable, TextInput, Select } from '@etherisc/ui-kit';
import { Button, StatusBadge } from '@etherisc/ui-kit';
import { 
  JobDisplayInfo, 
  JobStatus, 
  JobType, 
  JobListFilters, 
  JOB_TYPE_LABELS
} from '../../types/job';
import { JobService, jobService } from '../../services/climate/job/JobService';
import { ClimateApiError } from '../../services/climate/base/ClimateApiError';
import { RefreshCw, SearchIcon, FilterIcon } from 'lucide-react';

interface JobListPageProps {
  refreshInterval?: number; // Auto-refresh interval in milliseconds
}

// Job status badge component
const JobStatusBadge: React.FC<{ status: JobStatus }> = ({ status }) => {
  const getStatusVariant = (status: JobStatus): 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'info';
      case 'error':
      case 'failed':
      case 'timeout':
        return 'error';
      case 'cancelled':
      case 'paused':
      case 'partial':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const getStatusLabel = (status: JobStatus): string => {
    const statusMap: Record<JobStatus, string> = {
      'queued': 'Queued',
      'running': 'Running',
      'completed': 'Completed',
      'error': 'Error',
      'cancelled': 'Cancelled',
      'paused': 'Paused',
      'timeout': 'Timeout',
      'pending': 'Pending',
      'failed': 'Failed',
      'partial': 'Partial'
    };
    return statusMap[status] || status;
  };

  return (
    <StatusBadge variant={getStatusVariant(status)}>
      {getStatusLabel(status)}
    </StatusBadge>
  );
};

// Simple progress bar component
const JobProgressBar: React.FC<{ progress: number; status: JobStatus }> = ({ progress, status }) => {
  const getProgressColor = (status: JobStatus) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'running':
        return 'bg-blue-500';
      case 'error':
      case 'failed':
      case 'timeout':
        return 'bg-red-500';
      case 'cancelled':
      case 'paused':
        return 'bg-yellow-500';
      case 'partial':
        return 'bg-orange-500';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(status)}`}
        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
      />
    </div>
  );
};

export const JobListPage: React.FC<JobListPageProps> = ({ 
  refreshInterval = 30000 // 30 seconds default
}) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<JobDisplayInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<JobListFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [pageSize] = useState(25);

  // Calculate statistics
  const runningJobs = jobs.filter(job => job.status === 'running').length;
  const completedJobs = jobs.filter(job => job.status === 'completed').length;
  const failedJobs = jobs.filter(job => ['error', 'failed', 'timeout'].includes(job.status)).length;

  /**
   * Fetch jobs from API
   */
  const fetchJobs = async (page: number = currentPage, currentFilters: JobListFilters = filters) => {
    try {
      setLoading(true);
      setError(null);
      
      const offset = (page - 1) * pageSize;
      const response = await jobService.getJobs({
        ...currentFilters,
        limit: pageSize,
        offset: offset
      });

      if (response.success) {
        const transformedJobs = response.jobs.map(job => 
          JobService.transformJobForDisplay(job)
        );
        
        setJobs(transformedJobs);
        setTotalJobs(response.pagination.total_count);
      } else {
        setError('Failed to fetch jobs');
      }
    } catch (err) {
      if (err instanceof ClimateApiError) {
        setError(err.getUserMessage());
      } else {
        setError('An unexpected error occurred while fetching jobs');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle filter changes
   */
  const handleFilterChange = (newFilters: Partial<JobListFilters>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    setCurrentPage(1); // Reset to first page when filters change
    fetchJobs(1, updatedFilters);
  };

  /**
   * Handle job selection for detail view
   */
  const handleJobClick = (jobId: string) => {
    navigate(`/admin/jobs/${jobId}`);
  };

  /**
   * Format timestamp for display
   * Expects ISO 8601 UTC timestamps from server (e.g., "2025-12-09T11:49:54Z")
   * Automatically converts to user's local timezone
   */
  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString();
  };

  /**
   * Get relative time string
   * Expects ISO 8601 UTC timestamps from server (e.g., "2025-12-09T11:49:54Z")
   */
  const getRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return `${Math.floor(diffMinutes / 1440)}d ago`;
  };

  // Define table columns
  const columns = [
    {
      id: 'job_id',
      header: 'Job ID',
      accessorKey: 'id',
      enableSorting: true,
      size: 150,
      cell: ({ row }: { row: { original: JobDisplayInfo } }) => (
        <span className="font-mono text-sm">{row.original.id.slice(0, 12)}...</span>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      accessorKey: 'type',
      enableSorting: true,
      size: 150,
      cell: ({ row }: { row: { original: JobDisplayInfo } }) => (
        <span className="badge badge-outline text-xs">
          {JOB_TYPE_LABELS[row.original.type] || row.original.type}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      enableSorting: true,
      size: 120,
      cell: ({ row }: { row: { original: JobDisplayInfo } }) => (
        <JobStatusBadge status={row.original.status} />
      ),
    },
    {
      id: 'progress',
      header: 'Progress',
      accessorKey: 'progress',
      enableSorting: true,
      size: 150,
      cell: ({ row }: { row: { original: JobDisplayInfo } }) => (
        <div className="space-y-1">
          <JobProgressBar progress={row.original.progress} status={row.original.status} />
          <span className="text-sm text-gray-600">{row.original.progress}%</span>
        </div>
      ),
    },
    {
      id: 'message',
      header: 'Message',
      accessorKey: 'message',
      enableSorting: false,
      size: 300,
      cell: ({ row }: { row: { original: JobDisplayInfo } }) => (
        <span className="text-sm text-gray-700 truncate max-w-xs block" title={row.original.message}>
          {row.original.message}
        </span>
      ),
    },
    {
      id: 'new',
      header: 'Created',
      accessorKey: 'createdAt',
      enableSorting: true,
      size: 150,
      cell: ({ row }: { row: { original: JobDisplayInfo } }) => (
        <div className="text-sm">
          <div>{formatTimestamp(row.original.createdAt)}</div>
          <div className="text-xs text-gray-400">{getRelativeTime(row.original.createdAt)}</div>
        </div>
      ),
    },
    {
      id: 'duration',
      header: 'Duration',
      accessorKey: 'duration',
      enableSorting: false,
      size: 100,
      cell: ({ row }: { row: { original: JobDisplayInfo } }) => (
        <span className="text-sm">{row.original.duration || '-'}</span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      enableResizing: false,
      size: 100,
      cell: ({ row }: { row: { original: JobDisplayInfo } }) => (
        <Button
          onClick={() => handleJobClick(row.original.id)}
          intent="outline"
          size="sm"
        >
          Details
        </Button>
      ),
    },
  ];

  // Initial load
  useEffect(() => {
    fetchJobs();
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    if (refreshInterval > 0) {
      const interval = setInterval(() => {
        fetchJobs();
      }, refreshInterval);
      
      return () => clearInterval(interval);
    }
  }, [refreshInterval, currentPage, filters]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Management</h1>
          <p className="text-gray-600 mt-1">
            Monitor and manage climate data processing jobs
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => fetchJobs()}
            disabled={loading}
            intent="outline"
            size="sm"
            className="flex items-center space-x-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>
          {refreshInterval > 0 && (
            <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
              Auto-refresh: {refreshInterval / 1000}s
            </span>
          )}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-primary">{totalJobs}</div>
          <div className="text-sm text-base-content/70">Total Jobs</div>
        </div>
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-info">{runningJobs}</div>
          <div className="text-sm text-base-content/70">Running</div>
        </div>
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-success">{completedJobs}</div>
          <div className="text-sm text-base-content/70">Completed</div>
        </div>
        <div className="bg-base-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-error">{failedJobs}</div>
          <div className="text-sm text-base-content/70">Failed</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <FilterIcon className="w-5 h-5" />
            Search and Filters
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Status Filter */}
          <Select
            label="Status"
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'queued', label: 'Queued' },
              { value: 'running', label: 'Running' },
              { value: 'completed', label: 'Completed' },
              { value: 'error', label: 'Error' },
              { value: 'cancelled', label: 'Cancelled' },
              { value: 'paused', label: 'Paused' },
              { value: 'timeout', label: 'Timeout' },
              { value: 'pending', label: 'Pending' },
              { value: 'failed', label: 'Failed' },
              { value: 'partial', label: 'Partial' }
            ]}
            value={filters.status || 'all'}
            onValueChange={(value) => handleFilterChange({ status: value === 'all' ? undefined : value as JobStatus })}
            placeholder="All Statuses"
          />

          {/* Job Type Filter */}
          <Select
            label="Job Type"
            options={[
              { value: 'all', label: 'All Types' },
              { value: 'data_intake', label: 'Data Intake' },
              { value: 'data_processing', label: 'Data Processing' },
              { value: 'data_validation', label: 'Data Validation' },
              { value: 'data_export', label: 'Data Export' },
              { value: 'maintenance', label: 'Maintenance' },
              { value: 'cleanup', label: 'Cleanup' }
            ]}
            value={filters.job_type || 'all'}
            onValueChange={(value) => handleFilterChange({ job_type: value === 'all' ? undefined : value as JobType })}
            placeholder="All Types"
          />

          {/* Search Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-base-content/50 z-10" />
              <div className="pl-10">
                <TextInput
                  placeholder="Search by job ID or message..."
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange({ search: e.target.value || undefined })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Jobs</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <Button onClick={() => fetchJobs()} intent="outline">
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Jobs Table */}
      <DataTable
        data={jobs}
        columns={columns}
        enableSorting={true}
        enableResizing={true}
        loading={loading}
      />
    </div>
  );
}; 